import React, { useState, useEffect, useCallback } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import styled from 'styled-components';
import { X, Trash2, Plus, User, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import { positionAsync } from '../positions/positionPicker';
import Button from '../../components/ui/Button';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
`;

const FieldGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 18px;
  margin-bottom: 24px;
`;

const GridItem = styled.div`
  grid-column: span ${({ $span }) => $span || 12};
`;

const SectionLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`;

const StepCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  margin-bottom: 12px;
  position: relative;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StepBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary.main};
  color: #fff;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

const RemoveStepBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  background: none;
  color: #A0A8B4;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #ef4444; color: #ef4444; }
  &:disabled { opacity: 0.3; cursor: not-allowed; &:hover { border-color: #E0E4E8; color: #A0A8B4; } }
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  background: #F0F2F5;
  border-radius: 20px;
  padding: 3px;
  position: relative;
  height: 36px;
  cursor: pointer;
  user-select: none;
`;

const SwitchTrack = styled.div`
  position: absolute;
  top: 3px;
  left: ${({ $right }) => ($right ? 'calc(50% + 1px)' : '3px')};
  width: calc(50% - 4px);
  height: calc(100% - 6px);
  background: ${({ theme }) => theme.colors.primary.main};
  border-radius: 18px;
  transition: left 0.25s ease;
`;

const SwitchLabel = styled.span`
  flex: 1;
  text-align: center;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? '#fff' : '#6B7280')};
  position: relative;
  z-index: 1;
  transition: color 0.2s;
  padding: 0 8px;
`;

const AddStepBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 12px;
  border: 1.5px dashed #E0E4E8;
  border-radius: 6px;
  background: none;
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.main};
    background: ${({ theme }) => `${theme.colors.primary.main}08`};
  }
`;

const emptyStep = () => ({ approverType: 'person', approverId: null });

export default function ApprovalFlowDrawer({ open, onClose, initialValues, onSave }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [flowType, setFlowType] = useState(null);
  const [steps, setSteps] = useState([emptyStep()]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (initialValues) {
          setName(initialValues.name || '');
          setFlowType(initialValues.flowType || null);
          if (initialValues.steps && initialValues.steps.length > 0) {
            setSteps(initialValues.steps.map((s) => ({
              approverType: s.approverType || 'person',
              approverId: s.approverId || null,
            })));
          } else {
            setSteps([emptyStep()]);
          }
        } else {
          setName('');
          setFlowType(null);
          setSteps([emptyStep()]);
        }
        setErrors({});
      }, 170);
      return () => clearTimeout(timer);
    } else {
      setName('');
      setFlowType(null);
      setSteps([emptyStep()]);
      setErrors({});
    }
  }, [open, initialValues]);

  const flowTypeOptions = [
    { id: 'leave', name: t('approvalFlow.leave') },
    { id: 'expense', name: t('approvalFlow.expense') },
    { id: 'certification', name: t('approvalFlow.certification') },
    { id: 'employee-profile', name: t('approvalFlow.employeeProfile') },
    { id: 'visitor', name: t('approvalFlow.visitor') },
    { id: 'goal', name: t('approvalFlow.goal') },
    { id: 'ats-offer', name: t('approvalFlow.atsOffer') },
  ];

  const handleStepChange = useCallback((index, field, value) => {
    setSteps((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === 'approverType') {
        next[index].approverId = null;
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`step_${index}`];
      return next;
    });
  }, []);

  const handleAddStep = useCallback(() => {
    setSteps((prev) => [...prev, emptyStep()]);
  }, []);

  const handleRemoveStep = useCallback((index) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const searchEmployees = useCallback(async (query) => {
    try {
      const params = query ? { 'firstName[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      return (res.data?.data?.data || []).map((e) => ({
        id: e.id,
        name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        firstName: e.firstName,
        lastName: e.lastName,
        profilePicture: e.profilePicture,
      }));
    } catch {
      return [];
    }
  }, []);

  const getEmployeeById = useCallback(async (id) => {
    try {
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      if (!e) return null;
      return {
        id: e.id,
        name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        firstName: e.firstName,
        lastName: e.lastName,
        profilePicture: e.profilePicture,
      };
    } catch {
      return null;
    }
  }, []);

  // Pozisyon onaycısı: ortak zengin seçici — onay kimde duracağını pozisyonda oturan
  // kişiden görmek, koddan tahmin etmekten güvenlidir (../positions/positionPicker).
  const searchPositions = positionAsync.onSearch;
  const getPositionById = positionAsync.onGetById;

  const handleSubmit = async () => {
    const newErrors = {};

    if (!name.trim()) newErrors.name = true;
    if (!flowType) newErrors.flowType = true;

    steps.forEach((step, i) => {
      if (!step.approverId) newErrors[`step_${i}`] = true;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error(t('common.fillRequired', { defaultValue: 'Zorunlu alanlari doldurun.' }));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        flowType,
        steps: steps.map((s, i) => ({
          stepOrder: i + 1,
          approverType: s.approverType,
          approverId: s.approverId,
        })),
      };

      const success = await onSave?.(payload);
      if (success !== false) onClose?.();
    } catch {
      // handled by interceptor
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size="45%"
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100vw',
        minWidth: 380,
        margin: '12px',
        borderRadius: '6px',
        height: 'calc(100vh - 24px)',
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
      }}
    >
      <Header>
        <Title>{initialValues ? t('approvalFlow.edit') : t('approvalFlow.new')}</Title>
        <CloseBtn onClick={onClose}><X size={20} /></CloseBtn>
      </Header>

      <Body>
        <FieldGroup>
          <GridItem $span={12}>
            <TextInput
              label={t('approvalFlow.name')}
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: false })); }}
              required
              error={errors.name ? t('common.requiredField', { defaultValue: 'Bu alan zorunludur.' }) : undefined}
            />
          </GridItem>
          <GridItem $span={12}>
            <GenericSelect
              label={t('approvalFlow.flowType')}
              options={flowTypeOptions}
              value={flowType}
              onChange={(val) => { setFlowType(val); setErrors((p) => ({ ...p, flowType: false })); }}
              idKey="id"
              labelKey="name"
              required
              error={errors.flowType ? t('common.requiredField', { defaultValue: 'Bu alan zorunludur.' }) : undefined}
            />
          </GridItem>
        </FieldGroup>

        <SectionLabel>{t('approvalFlow.steps')}</SectionLabel>

        {steps.map((step, index) => (
          <StepCard key={index}>
            <StepHeader>
              <StepBadge>{index + 1}</StepBadge>
              <RemoveStepBtn
                onClick={() => handleRemoveStep(index)}
                disabled={steps.length <= 1}
                title={t('approvalFlow.removeStep')}
              >
                <Trash2 size={15} />
              </RemoveStepBtn>
            </StepHeader>

            <SwitchContainer
              onClick={() => handleStepChange(index, 'approverType', step.approverType === 'person' ? 'position' : 'person')}
            >
              <SwitchTrack $right={step.approverType === 'position'} />
              <SwitchLabel $active={step.approverType === 'person'}>
                {t('approvalFlow.approverPerson')}
              </SwitchLabel>
              <SwitchLabel $active={step.approverType === 'position'}>
                {t('approvalFlow.approverPosition')}
              </SwitchLabel>
            </SwitchContainer>

            <AsyncGenericSelect
              label={t('approvalFlow.selectApprover')}
              value={step.approverId}
              onChange={(val) => handleStepChange(index, 'approverId', val)}
              onSearch={step.approverType === 'person' ? searchEmployees : searchPositions}
              onGetById={step.approverType === 'person' ? getEmployeeById : getPositionById}
              renderOption={step.approverType === 'person'
                ? (opt) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{opt.name}</span>
                  </div>
                )
                : positionAsync.renderOption
              }
              searchPlaceholder={step.approverType === 'person' ? undefined : t('position.pickerSearchPlaceholder')}
              required
              error={errors[`step_${index}`] ? t('common.requiredField', { defaultValue: 'Bu alan zorunludur.' }) : undefined}
            />
          </StepCard>
        ))}

        <AddStepBtn onClick={handleAddStep} type="button">
          <Plus size={15} />
          {t('approvalFlow.addStep')}
        </AddStepBtn>
      </Body>

      <Footer>
        <Button variant="outline" onClick={onClose} disabled={saving}>
          {t('common.cancel', { defaultValue: 'Iptal' })}
        </Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? t('common.loading') : t('common.save', { defaultValue: 'Kaydet' })}
        </Button>
      </Footer>
    </Drawer>
  );
}
